import { ICreatePassportDto, TCreatePassportValueDTO } from '@/pages/Equipment/equipment.types'
import { IPassportSchemaField } from '@/pages/MDM/mdm.types'
import mdmUtils from '@/pages/MDM/mdm.utils'
import { useMemo } from 'react'

interface IProps {
  prevPassportValues: ICreatePassportDto['values']
  passportValues: ICreatePassportDto['values']
}

export type TEquipmentPassportDifferences = Record<
  IPassportSchemaField['id'],
  { left: TCreatePassportValueDTO; right: TCreatePassportValueDTO; isChanged: boolean }
>

const usePassportDifferences = (props: IProps) => {
  const passportDifferences = useMemo((): TEquipmentPassportDifferences => {
    if (!Boolean(props.passportValues) || !Boolean(props.prevPassportValues)) return {}

    const res: TEquipmentPassportDifferences = {}
    const primaryList =
      props.passportValues.length > props.prevPassportValues.length ? props.passportValues : props.prevPassportValues

    const leftValueByFieldId = new Map(props.prevPassportValues.map((item) => [item.fieldId, item]))
    const rightValueByFieldId = new Map(props.passportValues.map((item) => [item.fieldId, item]))

    const emptyPassportValue = mdmUtils.generateCreatePassportValueDTO()

    for (let value of primaryList) {
      res[value.fieldId] = {
        left: leftValueByFieldId.has(value.fieldId)
          ? leftValueByFieldId.get(value.fieldId)
          : mdmUtils.generateCreatePassportValueDTO(value.fieldId),
        right: rightValueByFieldId.has(value.fieldId)
          ? rightValueByFieldId.get(value.fieldId)
          : mdmUtils.generateCreatePassportValueDTO(value.fieldId),
        isChanged: !mdmUtils.isEquipmentPassportValuesEqual(
          leftValueByFieldId.get(value.fieldId) ?? emptyPassportValue,
          rightValueByFieldId.get(value.fieldId) ?? emptyPassportValue
        ),
      }
    }

    return res
  }, [props.prevPassportValues, props.passportValues])

  const changedPassportSchemaFieldIds = useMemo(() => {
    return Object.entries(passportDifferences)
      .filter(([_, value]) => value.isChanged)
      .map((item) => Number(item[0]))
  }, [passportDifferences])

  return { passportDifferences, changedPassportSchemaFieldIds }
}

export default usePassportDifferences
